import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, articleCategories } from '@/mocks/articles';

const detailImages: Record<string, string> = {
  'article-01-img-01': 'https://readdy.ai/api/search-image?query=Oriental%20brand%20studio%20photography%2C%20vintage%20books%20and%20dried%20herbs%20on%20wooden%20desk%2C%20warm%20natural%20lighting%2C%20minimal%20composition%2C%20cream%20background%2C%20poetic%20atmosphere&width=900&height=500&seq=art-detail-01a&orientation=landscape',
  'article-01-img-02': 'https://readdy.ai/api/search-image?query=Natural%20skincare%20product%20photography%20with%20botanical%20ingredients%2C%20warm%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20cream%20background%2C%20zen%20atmosphere&width=900&height=500&seq=art-detail-01b&orientation=landscape',
  'article-02-img-01': 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20herbal%20medicine%20photography%2C%20dried%20roots%20and%20flowers%20on%20rice%20paper%2C%20warm%20amber%20lighting%2C%20minimal%20composition&width=900&height=500&seq=art-detail-02a&orientation=landscape',
  'article-02-img-02': 'https://readdy.ai/api/search-image?query=Modern%20skincare%20laboratory%20with%20botanical%20extracts%2C%20warm%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20clean%20cream%20background&width=900&height=500&seq=art-detail-02b&orientation=landscape',
  'article-03-img-01': 'https://readdy.ai/api/search-image?query=Elegant%20tea%20ceremony%20setup%20with%20teapot%20and%20cups%20on%20bamboo%20mat%2C%20warm%20natural%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20serene%20atmosphere&width=900&height=500&seq=art-detail-03a&orientation=landscape',
  'article-03-img-02': 'https://readdy.ai/api/search-image?query=Four%20seasons%20tea%20collection%20in%20ceramic%20bowls%2C%20warm%20natural%20lighting%2C%20minimal%20oriental%20composition%2C%20soft%20cream%20background&width=900&height=500&seq=art-detail-03b&orientation=landscape',
  'article-04-img-01': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20poetry%20book%20with%20brush%20and%20ink%2C%20warm%20natural%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20cream%20background&width=900&height=500&seq=art-detail-04a&orientation=landscape',
  'article-04-img-02': 'https://readdy.ai/api/search-image?query=Minimalist%20oriental%20home%20decor%20with%20ceramic%20vase%20and%20branch%2C%20warm%20natural%20lighting%2C%20clean%20cream%20walls%2C%20zen%20simplicity&width=900&height=500&seq=art-detail-04b&orientation=landscape',
  'article-05-img-01': 'https://readdy.ai/api/search-image?query=Oriental%20fragrance%20workshop%20with%20essential%20oils%20and%20dried%20flowers%2C%20warm%20natural%20lighting%2C%20minimal%20composition%2C%20cream%20background&width=900&height=500&seq=art-detail-05a&orientation=landscape',
  'article-05-img-02': 'https://readdy.ai/api/search-image?query=Elegant%20perfume%20bottles%20with%20peach%20blossoms%2C%20warm%20golden%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20soft%20cream%20background&width=900&height=500&seq=art-detail-05b&orientation=landscape',
  'article-06-img-01': 'https://readdy.ai/api/search-image?query=Minimalist%20living%20room%20with%20negative%20space%20design%2C%20warm%20natural%20lighting%2C%20oriental%20aesthetic%2C%20cream%20walls%2C%20wooden%20furniture&width=900&height=500&seq=art-detail-06a&orientation=landscape',
  'article-06-img-02': 'https://readdy.ai/api/search-image?query=Oriental%20home%20decor%20with%20indoor%20plants%20and%20ceramic%20objects%2C%20warm%20natural%20lighting%2C%20minimal%20composition%2C%20soft%20cream%20background&width=900&height=500&seq=art-detail-06b&orientation=landscape',
};

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-950">文章未找到</h1>
            <Link to="/articles" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              返回文章列表 &rarr;
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '呦呦 YouYou',
    },
    url: `https://yooyoozone.github.io/articles/${article.slug}`,
  };

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        canonicalUrl={`https://yooyoozone.github.io/articles/${article.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 md:pt-24">
        <article className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章', path: '/articles' },
              { label: article.title },
            ]}
          />

          <header className="mb-10">
            <div className="flex items-center gap-2 text-xs text-foreground-400 mb-4">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span>{article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full">
                {articleCategories.find((c) => c.id === article.category)?.name}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs bg-secondary-100 text-secondary-600 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="prose-custom">
            {article.content.map((section, idx) => {
              if (section.type === 'heading') {
                return (
                  <h2 key={idx} className="font-heading text-xl font-semibold text-foreground-900 mt-10 mb-4">
                    {section.content}
                  </h2>
                );
              }
              if (section.type === 'paragraph') {
                return (
                  <p key={idx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                    {section.content}
                  </p>
                );
              }
              if (section.type === 'quote') {
                return (
                  <blockquote key={idx} className="border-l-4 border-primary-300 pl-5 py-2 my-8 text-sm md:text-base text-foreground-500 italic leading-relaxed bg-primary-50/50 rounded-r-lg">
                    {section.content}
                  </blockquote>
                );
              }
              if (section.type === 'image' && section.imageSeq) {
                return (
                  <figure key={idx} className="my-8">
                    <img
                      src={detailImages[section.imageSeq]}
                      alt={article.title}
                      title={article.title}
                      className="w-full rounded-lg"
                    />
                  </figure>
                );
              }
              return null;
            })}
          </div>

          <footer className="mt-16 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-500">作者：{article.author}</p>
                <p className="text-xs text-foreground-400 mt-1">发布于 {article.date}</p>
              </div>
              <Link
                to="/articles"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
              >
                &larr; 返回文章列表
              </Link>
            </div>
          </footer>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-16">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/articles/${ra.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={articleCoverImagesForRelated[ra.coverImageSeq]}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-4">
                      <p className="font-heading text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {ra.title}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}

const articleCoverImagesForRelated: Record<string, string> = {
  'article-01': 'https://readdy.ai/api/search-image?query=Elegant%20oriental%20aesthetic%20editorial%20photography%2C%20warm%20earth%20tones%2C%20minimal%20composition%20with%20natural%20textures%2C%20soft%20diffused%20lighting%2C%20calm%20poetic%20zen%20atmosphere&width=600&height=400&seq=art-rel-01&orientation=landscape',
  'article-02': 'https://readdy.ai/api/search-image?query=Oriental%20botanical%20editorial%20photography%20of%20fresh%20herbs%20and%20flowers%20on%20rice%20paper%2C%20warm%20amber%20tones%2C%20minimal%20composition&width=600&height=400&seq=art-rel-02&orientation=landscape',
  'article-03': 'https://readdy.ai/api/search-image?query=Elegant%20tea%20ceremony%20editorial%20photography%2C%20steaming%20teacup%20on%20wooden%20table%2C%20warm%20golden%20lighting%2C%20minimal%20oriental%20aesthetic&width=600&height=400&seq=art-rel-03&orientation=landscape',
  'article-04': 'https://readdy.ai/api/search-image?query=Minimalist%20oriental%20living%20space%20editorial%20photography%2C%20ceramic%20vase%20with%20single%20branch%2C%20warm%20natural%20lighting&width=600&height=400&seq=art-rel-04&orientation=landscape',
  'article-05': 'https://readdy.ai/api/search-image?query=Oriental%20fragrance%20editorial%20photography%2C%20essential%20oil%20bottles%20with%20dried%20lavender%2C%20warm%20golden%20lighting&width=600&height=400&seq=art-rel-05&orientation=landscape',
  'article-06': 'https://readdy.ai/api/search-image?query=Minimalist%20oriental%20interior%20photography%2C%20clean%20living%20room%20with%20wooden%20furniture%2C%20warm%20natural%20lighting&width=600&height=400&seq=art-rel-06&orientation=landscape',
};